import type { ApiClient } from '../http/apiClient';
import type { IdentityProvider } from '../../auth/identityProvider';
import { buildMetaInfo } from '../model/metaInfo';
import type {
  KnowledgeBaseRetrieveAndGenerateRequest,
  KnowledgeBaseRetrieveAndGenerateResponse,
  KnowledgeBaseRetrieveRequest,
  KnowledgeBaseRetrieveResponse,
  KnowledgeBaseListKnowledgeBasesRequest,
  KnowledgeBaseListKnowledgeBasesResponse,
  KnowledgeBaseStartIngestionRequest,
  KnowledgeBaseStartIngestionResponse,
  KnowledgeBaseUploadDocumentRequest,
  KnowledgeBaseUploadDocumentResponse,
  KnowledgeBaseProvisionRequest,
  KnowledgeBaseProvisionResponse,
  RegisterKnowledgeBaseRequest,
  RegisterKnowledgeBaseResponse,
  ListTenantKnowledgeBasesRequest,
  ListTenantKnowledgeBasesResponse,
} from '../model/types';

/**
 * KnowledgeBase (Bedrock RAG) client — one method per modeled Knowledge Base
 * operation (RetrieveAndGenerate, Retrieve, ListKnowledgeBases, StartIngestion,
 * UploadDocument). Paths mirror model/knowledgebase/operations.smithy exactly.
 *
 * Several responses carry a JSON *string* payload (`citations`, `results`,
 * `body`) — the {@link KnowledgeBaseApi} static helpers parse them into the
 * normalized shapes below so features never hand-parse Bedrock JSON.
 *
 * Calls {@link ApiClient} only. Pure TS — no React. jobCategory is
 * KNOWLEDGE_DISCOVERY for retrieval/list/ingest ops and AI_ASSIST for the
 * grounded-generation op.
 */

const PATHS = {
  retrieveAndGenerate: '/knowledge-base/retrieve-and-generate',
  retrieve: '/knowledge-base/retrieve',
  list: '/knowledge-base/list',
  startIngestion: '/knowledge-base/start-ingestion',
  uploadDocument: '/knowledge-base/upload-document',
  provision: '/knowledge-base/provision',
  register: '/knowledge-base/register',
  listTenant: '/knowledge-base/list-tenant',
} as const;

// ---------------------------------------------------------------------------
// Normalized (parsed) shapes for the string JSON payloads
// ---------------------------------------------------------------------------

/** A single retrieved-reference source (from citations or a retrieve result). */
export interface KbSource {
  /** The retrieved chunk text (snippet). */
  text: string;
  /** Best-effort source URI (S3 object, web URL, …), when present. */
  uri?: string;
  /** Location type reported by Bedrock (S3, WEB, CONFLUENCE, …). */
  locationType?: string;
  /** Relevance score, when Bedrock supplies one (retrieve results). */
  score?: number;
}

/** A parsed citation: a span of the generated answer + its supporting sources. */
export interface KbCitation {
  /** The generated text span this citation supports (may be empty). */
  text: string;
  sources: KbSource[];
}

/** A parsed knowledge-base summary (from ListKnowledgeBases). */
export interface KbSummary {
  knowledgeBaseId: string;
  name: string;
  description?: string;
  status?: string;
  updatedAt?: string;
}

/** Parsed ListKnowledgeBases payload. */
export interface KbListResult {
  knowledgeBases: KbSummary[];
  nextToken?: string;
}

export interface RetrieveAndGenerateArgs {
  query: string;
  sessionId?: string;
  /**
   * Optional bundle-key override stamped into
   * `MetaInfo.executionResourceTypeBundle.storeBundleInfoIdentifier` so the
   * backend `resolveKnowledgeBase` routes this query to a section-scoped KB
   * (e.g. "goal"), separate from the team/architecture KB. When omitted, the
   * active-team bundle key is used (the pre-existing behavior).
   */
  bundleKey?: string;
}

export interface RetrieveArgs {
  query: string;
  numberOfResults?: number;
  /** Optional bundle-key override (see {@link RetrieveAndGenerateArgs.bundleKey}). */
  bundleKey?: string;
}

export interface ListKnowledgeBasesArgs {
  maxResults?: number;
  nextToken?: string;
}

/** Arguments to StartIngestion (KB + data source resolved server-side). */
export interface StartIngestionArgs {
  /** Optional bundle-key override (see {@link RetrieveAndGenerateArgs.bundleKey}). */
  bundleKey?: string;
}

export interface UploadDocumentArgs {
  /** RELATIVE object key — the backend prefixes it with the configured bucket prefix and validates it. */
  objectKey: string;
  content: string;
  contentType?: string;
  /** Optional bundle-key override (see {@link RetrieveAndGenerateArgs.bundleKey}). */
  bundleKey?: string;
}

/** Arguments to StartKnowledgeBaseProvisioning. */
export interface StartKnowledgeBaseProvisioningArgs {
  /** Logical section the KB is provisioned for (e.g. "goal"). Idempotent. */
  sectionKey: string;
}

/** Normalized result of StartKnowledgeBaseProvisioning. */
export interface KnowledgeBaseProvisioningResult {
  /** Step Functions execution ARN to poll; '' when already provisioned. */
  executionArn: string;
  /** True when the KB already exists (ready immediately, no workflow). */
  alreadyProvisioned: boolean;
  /** The resolved/created knowledge base id ('' when not reported). */
  knowledgeBaseId: string;
}

/**
 * A parsed registered tenant knowledge base (from ListTenantKnowledgeBases).
 * Mirrors an entry of VectorServiceConfig.knowledgeBaseBundleInfoMap — the
 * non-secret bundle-key → knowledge-base mapping. Distinct from {@link KbSummary}
 * (which describes a Bedrock KB present in the account).
 */
export interface TenantKnowledgeBase {
  /** Logical bundle key the KB is registered/routed under. */
  bundleKey: string;
  knowledgeBaseId: string;
  dataSourceId?: string;
  bucket?: string;
  bucketPrefix?: string;
  modelArn?: string;
}

/** Parsed ListTenantKnowledgeBases payload. */
export interface TenantKbListResult {
  knowledgeBases: TenantKnowledgeBase[];
}

/** Arguments to register a tenant Bedrock KB under a logical bundle key. */
export interface RegisterKnowledgeBaseArgs {
  bundleKey: string;
  knowledgeBaseId: string;
  dataSourceId: string;
  bucket: string;
  /** Required object-key prefix within the bucket (may be empty). */
  bucketPrefix?: string;
  modelArn: string;
}

/** Extracts a best-effort source URI from a Bedrock `location` object. */
function extractUri(location: unknown): { uri?: string; type?: string } {
  if (!location || typeof location !== 'object') return {};
  const loc = location as Record<string, unknown>;
  const type = typeof loc.type === 'string' ? loc.type : undefined;
  // Bedrock nests the concrete location under a type-specific key.
  for (const key of Object.keys(loc)) {
    const val = loc[key];
    if (val && typeof val === 'object') {
      const nested = val as Record<string, unknown>;
      const candidate =
        (typeof nested.uri === 'string' && nested.uri) ||
        (typeof nested.url === 'string' && nested.url) ||
        undefined;
      if (candidate) return { uri: candidate, ...(type ? { type } : {}) };
    }
  }
  return type ? { type } : {};
}

/** Normalizes a Bedrock retrievedReference / retrievalResult into a KbSource. */
function toSource(ref: unknown): KbSource {
  const r = (ref ?? {}) as Record<string, unknown>;
  const content = (r.content ?? {}) as Record<string, unknown>;
  const text = typeof content.text === 'string' ? content.text : '';
  const { uri, type } = extractUri(r.location);
  const score = typeof r.score === 'number' ? r.score : undefined;
  return {
    text,
    ...(uri ? { uri } : {}),
    ...(type ? { locationType: type } : {}),
    ...(score !== undefined ? { score } : {}),
  };
}

/**
 * Builds the partial buildMetaInfo argument that stamps a section-scoped bundle
 * key into `executionResourceTypeBundle.storeBundleInfoIdentifier`. An explicit
 * bundle key takes precedence over the active-team bundle key in buildMetaInfo,
 * so goal-scoped KB ops route to the goal KB regardless of the active team. A
 * blank/omitted key leaves the argument empty so the active-team behavior is
 * preserved unchanged.
 */
function bundleOverride(
  bundleKey?: string,
): { executionResourceTypeBundle?: { storeBundleInfoIdentifier: string } } {
  const key = (bundleKey ?? '').trim();
  return key
    ? { executionResourceTypeBundle: { storeBundleInfoIdentifier: key } }
    : {};
}

export class KnowledgeBaseApi {
  constructor(
    private readonly apiClient: ApiClient,
    private readonly identityProvider: IdentityProvider,
  ) {}

  // -------------------------------------------------------------------------
  // Static parse helpers for the string JSON payloads
  // -------------------------------------------------------------------------

  /** Parses the `citations` JSON string of a RetrieveAndGenerate response. */
  static parseCitations(citations?: string): KbCitation[] {
    if (!citations) return [];
    let raw: unknown;
    try {
      raw = JSON.parse(citations);
    } catch {
      return [];
    }
    const list = Array.isArray(raw)
      ? raw
      : Array.isArray((raw as Record<string, unknown>)?.citations)
        ? ((raw as Record<string, unknown>).citations as unknown[])
        : [];
    return list.map((c) => {
      const cit = (c ?? {}) as Record<string, unknown>;
      const gen = (cit.generatedResponsePart ?? {}) as Record<string, unknown>;
      const part = (gen.textResponsePart ?? {}) as Record<string, unknown>;
      const text = typeof part.text === 'string' ? part.text : '';
      const refs = Array.isArray(cit.retrievedReferences)
        ? (cit.retrievedReferences as unknown[])
        : [];
      return { text, sources: refs.map(toSource) };
    });
  }

  /** Parses the `results` JSON string of a Retrieve response into sources. */
  static parseRetrieveResults(results?: string): KbSource[] {
    if (!results) return [];
    let raw: unknown;
    try {
      raw = JSON.parse(results);
    } catch {
      return [];
    }
    const list = Array.isArray(raw)
      ? raw
      : Array.isArray((raw as Record<string, unknown>)?.retrievalResults)
        ? ((raw as Record<string, unknown>).retrievalResults as unknown[])
        : [];
    return list.map(toSource);
  }

  /** Parses the `body` JSON string of a ListKnowledgeBases response. */
  static parseKnowledgeBaseList(body?: string): KbListResult {
    if (!body) return { knowledgeBases: [] };
    let raw: unknown;
    try {
      raw = JSON.parse(body);
    } catch {
      return { knowledgeBases: [] };
    }
    const obj = (raw ?? {}) as Record<string, unknown>;
    const list = Array.isArray(raw)
      ? (raw as unknown[])
      : Array.isArray(obj.knowledgeBaseSummaries)
        ? (obj.knowledgeBaseSummaries as unknown[])
        : Array.isArray(obj.knowledgeBases)
          ? (obj.knowledgeBases as unknown[])
          : [];
    const knowledgeBases: KbSummary[] = list.map((k) => {
      const s = (k ?? {}) as Record<string, unknown>;
      return {
        knowledgeBaseId:
          (typeof s.knowledgeBaseId === 'string' && s.knowledgeBaseId) ||
          (typeof s.id === 'string' && s.id) ||
          '',
        name: typeof s.name === 'string' ? s.name : '',
        ...(typeof s.description === 'string'
          ? { description: s.description }
          : {}),
        ...(typeof s.status === 'string' ? { status: s.status } : {}),
        ...(typeof s.updatedAt === 'string' ? { updatedAt: s.updatedAt } : {}),
      };
    });
    const nextToken =
      typeof obj.nextToken === 'string' ? obj.nextToken : undefined;
    return { knowledgeBases, ...(nextToken ? { nextToken } : {}) };
  }

  /**
   * Parses the `body` JSON string of a ListTenantKnowledgeBases response into
   * the registered bundle-key → knowledge-base mapping. Tolerates three shapes:
   * a bare array of entries, an object with a `knowledgeBases`/`bundles` array,
   * or a plain object map keyed by bundle key ({ bundleKey: { ... } }).
   */
  static parseTenantKnowledgeBaseList(body?: string): TenantKbListResult {
    if (!body) return { knowledgeBases: [] };
    let raw: unknown;
    try {
      raw = JSON.parse(body);
    } catch {
      return { knowledgeBases: [] };
    }

    const toEntry = (key: string | undefined, v: unknown): TenantKnowledgeBase => {
      const s = (v ?? {}) as Record<string, unknown>;
      const bundleKey =
        (typeof s.bundleKey === 'string' && s.bundleKey) ||
        (typeof s.storeBundleInfoIdentifier === 'string' &&
          s.storeBundleInfoIdentifier) ||
        key ||
        '';
      return {
        bundleKey,
        knowledgeBaseId:
          (typeof s.knowledgeBaseId === 'string' && s.knowledgeBaseId) ||
          (typeof s.id === 'string' && s.id) ||
          '',
        ...(typeof s.dataSourceId === 'string'
          ? { dataSourceId: s.dataSourceId }
          : {}),
        ...(typeof s.bucket === 'string' ? { bucket: s.bucket } : {}),
        ...(typeof s.bucketPrefix === 'string'
          ? { bucketPrefix: s.bucketPrefix }
          : {}),
        ...(typeof s.modelArn === 'string' ? { modelArn: s.modelArn } : {}),
      };
    };

    if (Array.isArray(raw)) {
      return { knowledgeBases: raw.map((v) => toEntry(undefined, v)) };
    }
    const obj = (raw ?? {}) as Record<string, unknown>;
    const arr = Array.isArray(obj.knowledgeBases)
      ? (obj.knowledgeBases as unknown[])
      : Array.isArray(obj.bundles)
        ? (obj.bundles as unknown[])
        : null;
    if (arr) {
      return { knowledgeBases: arr.map((v) => toEntry(undefined, v)) };
    }
    // Fall back to treating the object as a bundleKey → entry map. Ignore a
    // nested `nextToken`/scalar members that aren't entry objects.
    const source =
      obj.knowledgeBaseBundleInfoMap &&
      typeof obj.knowledgeBaseBundleInfoMap === 'object'
        ? (obj.knowledgeBaseBundleInfoMap as Record<string, unknown>)
        : obj;
    const knowledgeBases: TenantKnowledgeBase[] = Object.entries(source)
      .filter(([, v]) => v && typeof v === 'object')
      .map(([k, v]) => toEntry(k, v));
    return { knowledgeBases };
  }

  // -------------------------------------------------------------------------
  // Operations
  // -------------------------------------------------------------------------

  async retrieveAndGenerate(
    args: RetrieveAndGenerateArgs,
  ): Promise<KnowledgeBaseRetrieveAndGenerateResponse> {
    const metaInfo = buildMetaInfo({
      identity: await this.identityProvider.getIdentity(),
      jobName: 'KnowledgeBaseRetrieveAndGenerate',
      jobCategory: 'AI_ASSIST',
      // requestType is contextual only; the backend RequestType enum has no
      // KB-specific values, so use the closest valid one (CONVERSE = grounded
      // generation).
      requestType: 'CONVERSE',
      ...bundleOverride(args.bundleKey),
    });
    return this.apiClient.post<
      KnowledgeBaseRetrieveAndGenerateRequest,
      KnowledgeBaseRetrieveAndGenerateResponse
    >(PATHS.retrieveAndGenerate, {
      metaInfo,
      query: args.query,
      ...(args.sessionId ? { sessionId: args.sessionId } : {}),
    });
  }

  async retrieve(args: RetrieveArgs): Promise<KnowledgeBaseRetrieveResponse> {
    const metaInfo = buildMetaInfo({
      identity: await this.identityProvider.getIdentity(),
      jobName: 'KnowledgeBaseRetrieve',
      jobCategory: 'KNOWLEDGE_DISCOVERY',
      // Closest valid backend RequestType (KB retrieve == semantic search).
      requestType: 'SEMANTIC_SEARCH',
      ...bundleOverride(args.bundleKey),
    });
    return this.apiClient.post<
      KnowledgeBaseRetrieveRequest,
      KnowledgeBaseRetrieveResponse
    >(PATHS.retrieve, {
      metaInfo,
      query: args.query,
      ...(args.numberOfResults !== undefined
        ? { numberOfResults: args.numberOfResults }
        : {}),
    });
  }

  async listKnowledgeBases(
    args: ListKnowledgeBasesArgs = {},
  ): Promise<KnowledgeBaseListKnowledgeBasesResponse> {
    const metaInfo = buildMetaInfo({
      identity: await this.identityProvider.getIdentity(),
      jobName: 'KnowledgeBaseListKnowledgeBases',
      jobCategory: 'KNOWLEDGE_DISCOVERY',
      // Closest valid backend RequestType (listing KBs ~ listing collections).
      requestType: 'LIST_COLLECTIONS',
    });
    return this.apiClient.post<
      KnowledgeBaseListKnowledgeBasesRequest,
      KnowledgeBaseListKnowledgeBasesResponse
    >(PATHS.list, {
      metaInfo,
      ...(args.maxResults !== undefined ? { maxResults: args.maxResults } : {}),
      ...(args.nextToken ? { nextToken: args.nextToken } : {}),
    });
  }

  async startIngestion(
    args: StartIngestionArgs = {},
  ): Promise<KnowledgeBaseStartIngestionResponse> {
    const metaInfo = buildMetaInfo({
      identity: await this.identityProvider.getIdentity(),
      jobName: 'KnowledgeBaseStartIngestion',
      jobCategory: 'KNOWLEDGE_DISCOVERY',
      // Closest valid backend RequestType (ingestion is part of the upload flow).
      requestType: 'UPLOAD_META_DATA',
      ...bundleOverride(args.bundleKey),
    });
    return this.apiClient.post<
      KnowledgeBaseStartIngestionRequest,
      KnowledgeBaseStartIngestionResponse
    >(PATHS.startIngestion, {
      metaInfo,
    });
  }

  async uploadDocument(
    args: UploadDocumentArgs,
  ): Promise<KnowledgeBaseUploadDocumentResponse> {
    const metaInfo = buildMetaInfo({
      identity: await this.identityProvider.getIdentity(),
      jobName: 'KnowledgeBaseUploadDocument',
      jobCategory: 'KNOWLEDGE_DISCOVERY',
      // Closest valid backend RequestType (uploading a KB document).
      requestType: 'UPLOAD_META_DATA',
      ...bundleOverride(args.bundleKey),
    });
    return this.apiClient.post<
      KnowledgeBaseUploadDocumentRequest,
      KnowledgeBaseUploadDocumentResponse
    >(PATHS.uploadDocument, {
      metaInfo,
      objectKey: args.objectKey,
      content: args.content,
      ...(args.contentType ? { contentType: args.contentType } : {}),
    });
  }

  /**
   * Idempotently provisions the section-scoped knowledge base for `sectionKey`
   * (e.g. "goal"). When the KB already exists the backend returns
   * `alreadyProvisioned = true` with no execution; otherwise it starts a Step
   * Functions workflow whose `executionArn` the caller polls via
   * {@link OrchestratorApi.describeExecution} until it SUCCEEDED/FAILED.
   *
   * metaInfo: jobCategory DOCUMENT_MANAGEMENT + the KNOWN-VALID contextual
   * requestType LIST_META_INFO — provisioning is not a CRUD/data op and the
   * backend RequestType enum has no provisioning value, so we reuse an accepted
   * value (as registerKnowledgeBase / team-onboarding do). requestType is
   * contextual only and does not affect behavior.
   */
  async startKnowledgeBaseProvisioning(
    args: StartKnowledgeBaseProvisioningArgs,
  ): Promise<KnowledgeBaseProvisioningResult> {
    const metaInfo = buildMetaInfo({
      identity: await this.identityProvider.getIdentity(),
      jobName: 'StartKnowledgeBaseProvisioning',
      jobCategory: 'DOCUMENT_MANAGEMENT',
      requestType: 'LIST_META_INFO',
    });
    const res = await this.apiClient.post<
      KnowledgeBaseProvisionRequest,
      KnowledgeBaseProvisionResponse
    >(PATHS.provision, {
      metaInfo,
      sectionKey: args.sectionKey,
    });
    return {
      executionArn: res.executionArn ?? '',
      alreadyProvisioned: res.alreadyProvisioned === true,
      knowledgeBaseId: res.knowledgeBaseId ?? '',
    };
  }

  /**
   * Registers the team's own Bedrock KB under a logical bundle key (merged into
   * the hosted VectorServiceConfig). Subsequent KnowledgeBase ops carrying that
   * bundle key are routed to it; the default KB is unchanged.
   *
   * metaInfo: jobCategory DOCUMENT_MANAGEMENT + the KNOWN-VALID contextual
   * requestType LIST_META_INFO — the backend RequestType enum has no
   * KB-registration value and rejects unknown enums, so we reuse an accepted
   * value (requestType is contextual only and does not affect behavior).
   */
  async registerKnowledgeBase(
    args: RegisterKnowledgeBaseArgs,
  ): Promise<RegisterKnowledgeBaseResponse> {
    const metaInfo = buildMetaInfo({
      identity: await this.identityProvider.getIdentity(),
      jobName: 'RegisterKnowledgeBase',
      jobCategory: 'DOCUMENT_MANAGEMENT',
      requestType: 'LIST_META_INFO',
    });
    return this.apiClient.post<
      RegisterKnowledgeBaseRequest,
      RegisterKnowledgeBaseResponse
    >(PATHS.register, {
      metaInfo,
      bundleKey: args.bundleKey,
      knowledgeBaseId: args.knowledgeBaseId,
      dataSourceId: args.dataSourceId,
      bucket: args.bucket,
      ...(args.bucketPrefix !== undefined
        ? { bucketPrefix: args.bucketPrefix }
        : {}),
      modelArn: args.modelArn,
    });
  }

  /**
   * Lists the registered tenant knowledge bases (the non-secret bundle-key →
   * knowledge-base mapping). The response `body` is a JSON string — parse with
   * {@link KnowledgeBaseApi.parseTenantKnowledgeBaseList}.
   *
   * metaInfo: DOCUMENT_MANAGEMENT + LIST_META_INFO (see registerKnowledgeBase).
   */
  async listTenantKnowledgeBases(): Promise<ListTenantKnowledgeBasesResponse> {
    const metaInfo = buildMetaInfo({
      identity: await this.identityProvider.getIdentity(),
      jobName: 'ListTenantKnowledgeBases',
      jobCategory: 'DOCUMENT_MANAGEMENT',
      requestType: 'LIST_META_INFO',
    });
    return this.apiClient.post<
      ListTenantKnowledgeBasesRequest,
      ListTenantKnowledgeBasesResponse
    >(PATHS.listTenant, {
      metaInfo,
    });
  }
}
